import InfiniteScroll from 'react-infinite-scroll-component';
import PlantCard from './PlantCard';

/**
 * PlantCatalogGrid - Infinite scroll grid of plant cards
 *
 * Displays plants in 2-column grid with infinite scroll pagination.
 * Loads 30 plants per batch.
 *
 * @param {Object} props - Component props
 * @param {Array} props.plants - Array of plant objects
 * @param {boolean} props.hasMore - Whether more plants are available
 * @param {Function} props.onLoadMore - Callback to load next batch
 * @param {boolean} props.loading - Whether initial load is in progress
 * @param {Function} props.onPlaceClick - Callback when plant "Platzieren" clicked
 * @param {Function} props.onDetailsClick - Callback when plant "Details" clicked
 */
const PlantCatalogGrid = ({
  plants,
  hasMore,
  onLoadMore,
  loading,
  onPlaceClick,
  onDetailsClick
}) => {
  // Loading state (initial load)
  if (loading && plants.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          <p className="text-sm text-gray-500">Lade Pflanzen...</p>
        </div>
      </div>
    );
  }

  // Empty state (no results)
  if (!loading && plants.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            <span className="text-2xl">🌱</span>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              Keine Pflanzen gefunden
            </h3>
            <p className="text-xs text-gray-500">
              Versuchen Sie andere Filter oder Suchbegriffe
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading spinner for infinite scroll
  const loader = (
    <div key="loader" className="flex justify-center py-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
    </div>
  );

  // End message
  const endMessage = (
    <div className="text-center py-4 text-sm text-gray-500">
      Alle Pflanzen geladen
    </div>
  );

  return (
    <div
      id="plant-catalog-scroll-container"
      className="overflow-y-auto px-4 pb-4"
      style={{ height: '560px' }} // 750px dialog - 190px header = 560px (shows 2×2 cards comfortably)
    >
      <InfiniteScroll
        dataLength={plants.length}
        next={onLoadMore}
        hasMore={hasMore}
        loader={loader}
        endMessage={endMessage}
        scrollableTarget="plant-catalog-scroll-container"
      >
        {/* 2-column grid */}
        <div className="grid grid-cols-2 gap-4">
          {plants.map((plant) => (
            <PlantCard
              key={plant.id}
              plant={plant}
              onPlaceClick={onPlaceClick}
              onDetailsClick={onDetailsClick}
            />
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
};

export default PlantCatalogGrid;
